import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Trash2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { TermInfoPopover } from '@/components/vocabulary/TermInfoPopover';
import { variantInterpretationToBadgeVariant } from '@/lib/clinicalBadges';
import { formatDate } from '@/lib/format';
import type { GeneticVariantViewModel } from '@/features/patient-record/types';

import { RowActions, RowActionButton } from '../_shared/RowActions';

interface VariantRowProps {
  readonly variant: GeneticVariantViewModel;
  readonly onEdit: () => void;
  readonly onDelete: () => void;
  /** Side the gene term-info popover opens toward (PTV2-1298); set by GeneticInfoSection. */
  readonly termInfoSide?: 'left' | 'right';
}

export function VariantRow({
  variant,
  onEdit,
  onDelete,
  termInfoSide = 'right',
}: VariantRowProps): ReactElement {
  const { t } = useTranslation();

  const metadataEntries = [
    {
      key: 'zygosity',
      label: t('patient.geneticInfo.fields.zygosity'),
      value: variant.zygosity,
    },
    {
      key: 'inheritance',
      label: t('patient.geneticInfo.fields.inheritance'),
      value:
        variant.inheritance !== null
          ? t(`patient.geneticInfo.inheritance.${variant.inheritance}`)
          : null,
    },
    {
      key: 'reportDate',
      label: t('patient.geneticInfo.fields.reportDate'),
      value:
        variant.reportDate !== null ? formatDate(variant.reportDate) : null,
    },
    {
      key: 'labName',
      label: t('patient.geneticInfo.fields.labName'),
      value: variant.labName,
    },
  ].filter(
    (entry): entry is { key: string; label: string; value: string } =>
      entry.value !== null && entry.value.length > 0,
  );

  return (
    <div className="flex items-start gap-2 group">
      <div className="flex-1 min-w-0 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-card-foreground">
            {variant.gene}
          </span>
          {/* PTV2-1298: HGNC term-info popover next to the gene. Uses the ontology id when the row
              carries one, else the symbol (which the HGNC endpoint also resolves). */}
          {variant.gene.length > 0 && (
            <TermInfoPopover
              vocabularyId="hgnc"
              termId={variant.raw.geneId ?? variant.gene}
              termLabel={variant.gene}
              side={termInfoSide}
            />
          )}
          <span className="text-sm text-muted-foreground">{variant.variant}</span>
          {variant.interpretation !== null && (
            <Badge variant={variantInterpretationToBadgeVariant(variant.interpretation)}>
              {variant.interpretation}
            </Badge>
          )}
          {variant.variantType !== null && (
            <Badge variant="outline">{variant.variantType}</Badge>
          )}
        </div>

        {metadataEntries.length > 0 && (
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 text-xs">
            {metadataEntries.map((entry) => (
              <div key={entry.key} className="flex gap-2">
                <dt className="text-muted-foreground">{entry.label}</dt>
                <dd className="text-card-foreground">{entry.value}</dd>
              </div>
            ))}
          </dl>
        )}

        {variant.associatedConditions.length > 0 && (
          <div className="text-xs text-muted-foreground">
            <span className="font-medium">
              {t('patient.geneticInfo.fields.associatedConditions')}:
            </span>{' '}
            {variant.associatedConditions.join(', ')}
          </div>
        )}

        {variant.acmgCriteria.length > 0 && (
          <div className="flex flex-wrap items-center gap-1 text-xs">
            <span className="text-muted-foreground">
              {t('patient.geneticInfo.fields.acmgCriteria')}:
            </span>
            {variant.acmgCriteria.map((criterion) => (
              <Badge key={criterion} variant="outline">
                {criterion}
              </Badge>
            ))}
          </div>
        )}

        {variant.comments !== null && variant.comments.length > 0 && (
          <p className="text-xs text-muted-foreground italic break-words">
            {variant.comments}
          </p>
        )}
      </div>

      <RowActions>
        <RowActionButton
          icon={<Pencil className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />}
          label={t('patient.geneticInfo.edit')}
          onClick={onEdit}
        />
        <RowActionButton
          icon={<Trash2 className="h-3.5 w-3.5 text-destructive" aria-hidden="true" />}
          label={t('patient.geneticInfo.delete')}
          onClick={onDelete}
        />
      </RowActions>
    </div>
  );
}
